import { fillTemplate, templateBlanks, type PromptTemplate, type UpsertPromptTemplateInput } from "@codvero/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BookMarked, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { api, del, get, post } from "@/api/client.ts";
import { cn } from "@/lib/cn.ts";
import { useConfirm } from "@/ui/ConfirmDialog.tsx";
import { Alert, Button, Field, Input } from "@/ui/primitives.tsx";
import { useApiError } from "@/ui/useApiError.ts";

/** These texts show the {{blank}} syntax itself: i18next must not fill it in. */
const RAW = { interpolation: { prefix: "[[[", suffix: "]]]" } } as const;

const templatesApi = {
  list: () => get<PromptTemplate[]>("/api/prompt-templates"),
  create: (input: UpsertPromptTemplateInput) => post<PromptTemplate>("/api/prompt-templates", input),
  update: (id: string, input: UpsertPromptTemplateInput) => api<PromptTemplate>(`/api/prompt-templates/${id}`, { method: "PUT", json: input }),
  remove: (id: string) => del(`/api/prompt-templates/${id}`),
};

/**
 * The saved-prompts button of a Chat section message box: pick one (the space's own first), fill its blanks
 * ({{name}}), and it lands in the box. "/" stays for MCP connections.
 */
export function TemplatesButton({ spaceId, onInsert }: { spaceId?: string | null; onInsert: (text: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [filling, setFilling] = useState<PromptTemplate | null>(null);
  const [editing, setEditing] = useState<PromptTemplate | "new" | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const q = useQuery({ queryKey: ["prompt-templates"], queryFn: templatesApi.list, staleTime: 60_000 });
  useEffect(() => {
    const h = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  // the space's templates, then the ones for everywhere (other spaces' are left out)
  const list = (q.data ?? []).filter((x) => !x.spaceId || x.spaceId === spaceId).sort((a, b) => Number(!!b.spaceId) - Number(!!a.spaceId) || a.title.localeCompare(b.title));
  const use = (tpl: PromptTemplate) => {
    setOpen(false);
    if (templateBlanks(tpl.body).length) setFilling(tpl);
    else onInsert(tpl.body);
  };
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-expanded={open} title={t("templates.title")} aria-label={t("templates.title")} onClick={() => setOpen(!open)} className={cn("grid size-8 place-items-center rounded-full border border-border text-fg-muted hover:text-fg", open && "border-accent text-accent")}>
        <BookMarked className="size-4" />
      </button>
      {open && (
        <div className="absolute bottom-full start-0 z-30 mb-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-surface text-sm shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs font-medium text-fg-muted">{t("templates.title")}</span>
            <button type="button" className="flex items-center gap-1 text-xs text-accent hover:underline" onClick={() => { setOpen(false); setEditing("new"); }}><Plus className="size-3.5" /> {t("templates.new")}</button>
          </div>
          {list.length === 0 && <p className="px-3 py-4 text-center text-xs text-fg-muted">{t("templates.empty", RAW)}</p>}
          <ul className="max-h-72 overflow-y-auto py-1">
            {list.map((x) => (
              <li key={x.id} className="group flex items-start gap-1 px-1">
                <button type="button" onClick={() => use(x)} className="min-w-0 flex-1 rounded-md px-2 py-1.5 text-start hover:bg-surface-2">
                  <span dir="auto" className="block truncate font-medium">{x.title}{x.spaceId && <span className="ms-1.5 rounded bg-accent-soft px-1 text-[10px] font-normal text-accent">{t("templates.thisSpace")}</span>}</span>
                  <span dir="auto" className="block truncate text-[11px] text-fg-muted">{x.body}</span>
                </button>
                <button type="button" aria-label={t("common.edit")} onClick={() => { setOpen(false); setEditing(x); }} className="mt-1.5 rounded p-1 text-fg-muted opacity-0 hover:text-fg group-hover:opacity-100 focus:opacity-100"><Pencil className="size-3.5" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* over the page, outside the message box's form: a form inside a form submits the outer one (a page reload) */}
      {filling && createPortal(<FillDialog tpl={filling} onCancel={() => setFilling(null)} onDone={(text) => { setFilling(null); onInsert(text); }} />, document.body)}
      {editing && createPortal(<EditDialog tpl={editing === "new" ? null : editing} spaceId={spaceId ?? null} onClose={() => setEditing(null)} />, document.body)}
    </div>
  );
}

function FillDialog({ tpl, onCancel, onDone }: { tpl: PromptTemplate; onCancel: () => void; onDone: (text: string) => void }) {
  const { t } = useTranslation();
  const blanks = templateBlanks(tpl.body);
  const [values, setValues] = useState<Record<string, string>>({});
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onCancel}>
      <form role="dialog" aria-modal="true" className="w-full max-w-md space-y-3 rounded-lg border border-border bg-surface p-4 shadow-xl" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); onDone(fillTemplate(tpl.body, values)); }}>
        <div className="flex items-center justify-between"><h2 dir="auto" className="font-semibold">{tpl.title}</h2><button type="button" aria-label={t("common.close")} onClick={onCancel}><X className="size-5" /></button></div>
        {blanks.map((b, i) => <Field key={b} label={b}><Input dir="auto" autoFocus={i === 0} required value={values[b] ?? ""} onChange={(e) => setValues({ ...values, [b]: e.target.value })} /></Field>)}
        <p dir="auto" className="whitespace-pre-wrap rounded-md bg-surface-2 p-2 text-xs text-fg-muted">{fillTemplate(tpl.body, Object.fromEntries(blanks.map((b) => [b, values[b] || `[${b}]`])))}</p>
        <div className="flex justify-end gap-2"><Button type="button" variant="ghost" onClick={onCancel}>{t("common.cancel")}</Button><Button type="submit">{t("templates.insert")}</Button></div>
      </form>
    </div>
  );
}

function EditDialog({ tpl, spaceId, onClose }: { tpl: PromptTemplate | null; spaceId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toMsg = useApiError();
  const qc = useQueryClient();
  const { confirm } = useConfirm();
  const [title, setTitle] = useState(tpl?.title ?? "");
  const [body, setBody] = useState(tpl?.body ?? "");
  const [onlyHere, setOnlyHere] = useState(tpl ? !!tpl.spaceId : false);
  const done = () => { void qc.invalidateQueries({ queryKey: ["prompt-templates"] }); onClose(); };
  const input = { title: title.trim(), body: body.trim(), spaceId: onlyHere ? (tpl?.spaceId ?? spaceId) : null };
  const save = useMutation({ mutationFn: () => (tpl ? templatesApi.update(tpl.id, input) : templatesApi.create(input)), onSuccess: done });
  const remove = useMutation({ mutationFn: () => templatesApi.remove(tpl!.id), onSuccess: done });
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <form role="dialog" aria-modal="true" className="w-full max-w-lg space-y-3 rounded-lg border border-border bg-surface p-4 shadow-xl" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); if (input.title && input.body) save.mutate(); }}>
        <div className="flex items-center justify-between"><h2 className="font-semibold">{tpl ? t("templates.edit") : t("templates.new")}</h2><button type="button" aria-label={t("common.close")} onClick={onClose}><X className="size-5" /></button></div>
        <Field label={t("templates.name")}><Input dir="auto" autoFocus required maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("templates.namePlaceholder")} /></Field>
        <Field label={t("templates.body")} hint={t("templates.bodyHint", RAW)}>
          <textarea dir="auto" rows={6} required value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("templates.bodyPlaceholder", RAW)} className="block w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm focus:border-accent focus:outline-none" />
        </Field>
        {templateBlanks(body).length > 0 && <p className="text-xs text-fg-muted">{t("templates.blanks")}: {templateBlanks(body).map((b) => <span key={b} className="me-1 rounded bg-surface-2 px-1.5 py-0.5">{b}</span>)}</p>}
        {(spaceId || tpl?.spaceId) && <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="accent-accent" checked={onlyHere} onChange={(e) => setOnlyHere(e.target.checked)} /> {t("templates.onlyThisSpace")}</label>}
        {(save.error || remove.error) && <Alert tone="danger">{toMsg(save.error ?? remove.error)}</Alert>}
        <div className="flex items-center gap-2">
          {tpl && <Button type="button" variant="ghost" loading={remove.isPending} onClick={async () => { if (await confirm({ title: t("templates.removeTitle"), message: t("templates.removeMsg", { name: tpl.title }), confirmLabel: t("common.delete"), danger: true })) remove.mutate(); }}><Trash2 className="size-4" /></Button>}
          <span className="flex-1" />
          <Button type="button" variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button type="submit" loading={save.isPending} disabled={!input.title || !input.body}>{t("common.save")}</Button>
        </div>
      </form>
    </div>
  );
}
